import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { Draggable } from "../../../common/types/Draggable";
import { Indicator } from "../../../common/types/Indicator";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Segmentation } from "../../../common/types/roots/fields";
import { isKf219CoMsuAggregationAvailable, kf219CoFilterOptions, kf219CoSegmentationOptions } from "../report-kf219-co";
import { Kf219CoFilterSettings } from "../settings/Kf219CoFilterSettings";
import { Kf219CoFilterSettingsForm } from "../settings-form/Kf219CoFilterSettingsForm";
import { Kf219CoTableDataRequest } from "../data-request/Kf219CoTableDataRequest";
import { Kf219CoJournalRequest } from "../journal/Kf219CoJournalRequest";
import { Kf219CoTableData } from "../table-data/Kf219CoTableData";

/** Разрезность отчёта — только КБК и Регион (без ГУ); недостающие элементы добавляются в конец неактивными */
function normalizeSegmentations(segmentations: Draggable<Segmentation>[] | null | undefined): Draggable<Segmentation>[] {
    const result: Draggable<Segmentation>[] = cloneDeep(
        (segmentations ?? []).filter(it => it.value === 'KBK' || it.value === 'REGION')
    )
    for (const option of kf219CoSegmentationOptions) {
        if (!result.some(it => it.value === option.value)) {
            result.push({ ...cloneDeep(option), active: false })
        }
    }
    return result
}

/** Поле "Округлять результат до" (b-form-input type=number) отдаёт строку; пустое значение = не задано */
function toRoundUpTo(roundUp: boolean, value: number | string | null): number | null {
    if (!roundUp || value === null || value === undefined || String(value).trim() === '') {
        return null
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
}

/**
 * Ключи видимых колонок таблицы (месяцы и 'total') в порядке вывода. Если колонки нет в сохранённых
 * индикаторах (другой период, новый отчёт) — она считается видимой.
 */
export function getKf219CoVisibleIndicatorKeys(months: string[], indicators: Indicator[]): string[] {
    return [...months, 'total'].filter(key => indicators.find(it => it.key === key)?.active ?? true)
}

export class Kf219CoFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(kf219CoFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидации
        if (settings.reportName !== 'KF219-CO') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'KF219-CO') {
            throw new Error('error_type_filter_options')
        }

        const reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        const reportType = options.reportTypes.find(it => it.value === settings.reportType)
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        const classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        const dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        const periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        const measureUnit = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnit) {
            throw new Error('error_not_found_measure_unit')
        }
        const mutuallyRedeeming = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeeming) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        const byForm = options.byForms.find(it => it.value === settings.byForm)
        if (!byForm) {
            throw new Error('error_not_found_by_form')
        }
        // Регион может отсутствовать в списке (список зависит от даты "по") — тогда поле остаётся пустым
        const regionOption = options.regions.find(it => it.value === settings.region) ?? null

        // 2. Формирование
        const form: Kf219CoFilterSettingsForm = {
            // Base:
            key: 'KF219-CO',
            reportName: cloneDeep(reportName),
            reportType: cloneDeep(reportType),
            classificationType: cloneDeep(classificationType),
            dataSource: cloneDeep(dataSource),
            periodicity: cloneDeep(periodicity),
            // Custom:
            periodFrom: new Date(settings.period.from),
            periodTo: new Date(settings.period.to),
            region: cloneDeep(regionOption),
            byForm: cloneDeep(byForm),
            measureUnit: cloneDeep(measureUnit),
            mutuallyRedeeming: cloneDeep(mutuallyRedeeming),
            mutuallyRedeemingLevels: cloneDeep(
                options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
            ),
            segmentations: normalizeSegmentations(settings.segmentations),
            incomes: cloneDeep(settings.incomes),
            roundUp: settings.roundUpTo != null,
            roundUpTo: settings.roundUpTo,
            msuAggregation: settings.msuAggregation ?? false,
            isTree: settings.isTree ?? true,
            fillCbc: settings.fillCbc ?? false,
            indicators: cloneDeep(settings.indicators ?? []),
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'KF219-CO') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        const roundUpTo: number | null = toRoundUpTo(form.roundUp, form.roundUpTo)

        const settings: Kf219CoFilterSettings = {
            // Base:
            reportName: 'KF219-CO',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            period: {
                from: moment(form.periodFrom).format('YYYY-MM-DD'),
                to: moment(form.periodTo).format('YYYY-MM-DD'),
            },
            region: form.region?.value ?? null,
            byForm: form.byForm.value,
            measureUnit: form.measureUnit.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: normalizeSegmentations(form.segmentations),
            incomes: cloneDeep(form.incomes),
            roundUpTo: roundUpTo,
            msuAggregation: form.msuAggregation ?? false,
            isTree: form.isTree,
            fillCbc: form.fillCbc ?? false,
            indicators: cloneDeep(form.indicators),
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(kf219CoFilterOptions)

        // С 1 января по вчерашний день; 1 января "вчера" — прошлый год, берём его начало, чтобы период не был обратным
        const yesterday = moment().subtract(1, 'days')
        const periodFrom = yesterday.clone().startOf('year').toDate()
        const periodTo = yesterday.toDate()

        const emptyForm: Kf219CoFilterSettingsForm = {
            key: 'KF219-CO',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'KF219-CO')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'PERIOD')!,
            // Custom fields
            periodFrom,
            periodTo,
            region: null,
            byForm: options.byForms.find(it => it.value === '2-19')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITH')!,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            segmentations: options.segmentations,
            incomes: options.incomes,
            roundUp: true,
            roundUpTo: 1,
            msuAggregation: true,
            isTree: true,
            fillCbc: false,
            indicators: [],
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидации
        if (form.key !== 'KF219-CO') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }
        if (moment(form.periodFrom).isAfter(form.periodTo, 'day')) {
            throw new Error('error_period_range_invalid')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (!['IISK', 'LOADER'].includes(form.dataSource.value)) {
            throw new Error('error_unsupported_data_source')
        }

        const segmentations: string[] = form.segmentations.filter(it => it.active).map(it => it.value)
        if (segmentations.length === 0) {
            throw new Error('error_input_segmentation')
        }
        // Если КБК в разрезности и все флажки блока "Доходы" сняты — формировать нечего
        if (segmentations.includes('KBK') && !form.incomes.some(it => it.active)) {
            throw new Error('error_input_at_least_one_kbk')
        }

        // ВЗК: при "с ВЗК" уровни не передаются
        let vzkList: number[] = []
        if (form.mutuallyRedeeming.value !== 'WITH') {
            vzkList = form.mutuallyRedeemingLevels.map(it => {
                if (it.value === 'TYPE-3') {
                    return 3
                } else if (it.value === 'TYPE-4') {
                    return 4
                } else {
                    throw new Error('error_unsupported_vzk_level')
                }
            })
        }

        const roundUpTo: number | null = toRoundUpTo(form.roundUp, form.roundUpTo)

        const params: Kf219CoTableDataRequest = {
            region: form.region.value,
            period_from: moment(form.periodFrom).format('YYYY-MM-DD'),
            period_to: moment(form.periodTo).format('YYYY-MM-DD'),
            source: form.dataSource.value,
            mutually_redeeming: form.mutuallyRedeeming.value,
            vzk_list: vzkList,
            measure_unit: form.measureUnit.value,
            segmentations: segmentations,
            incomes: cloneDeep(form.incomes),
            // Переключатель мог остаться включённым при скрытом поле — тогда агрегация не применяется
            msu_aggregation: (form.msuAggregation ?? false) && isKf219CoMsuAggregationAvailable(form.region.value, form.segmentations),
            tree_view: form.isTree,
            fill_cbc: form.isTree && (form.fillCbc ?? false),
            round_up_to: roundUpTo,
            locale: i18nService.locale,
        }

        // 2. Данные для таблицы
        const response = await fetch('/api-py/monitoring/reports-constructor/kf219-co', {
            method: 'POST',
            body: JSON.stringify(params),
        })
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const tableData: Kf219CoTableData = await response.json()

        // 3. Параметры выгрузки в журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: Kf219CoJournalRequest = {
            reportName: 'KF219-CO',
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // для Excel — поля экрана
            ...cloneDeep(params),
            // Может быть изменён во время превью (выпадающий список "Скрыть показатели")
            indicators_order: getKf219CoVisibleIndicatorKeys(tableData.months ?? [], form.indicators),
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams,
        }
        return result
    }
}
